/**
 * Focus Flow — mission hooks.
 *
 * React Query wrapper around the mission API + local-storage mirror.
 * Every mutation returns the fresh mission and updates the LS mirror
 * inside the mutation observer (so navigation on completion does not
 * drop the write).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import * as api from "./api";
import {
  clearLocalMission,
  reconcileMission,
  writeLocalMission,
} from "./memory";
import type {
  Mission,
  MissionCompleteResponse,
  MissionProgress,
  MissionStage,
} from "./types";

export const missionKeys = {
  all: () => ["missions"] as const,
  list: (workspaceId: string | null) =>
    ["missions", "list", workspaceId] as const,
  detail: (workspaceId: string | null, missionId: string | null) =>
    ["missions", "detail", workspaceId, missionId] as const,
  events: (workspaceId: string | null, missionId: string | null) =>
    ["missions", "events", workspaceId, missionId] as const,
};

export function useResumableMissions(workspaceId: string | null) {
  return useQuery({
    queryKey: missionKeys.list(workspaceId),
    queryFn: () => api.listResumableMissions(workspaceId!),
    enabled: Boolean(workspaceId),
    staleTime: 30_000,
  });
}

export function useMissionQuery(
  workspaceId: string | null,
  missionId: string | null,
  userId: string | null,
) {
  return useQuery({
    queryKey: missionKeys.detail(workspaceId, missionId),
    enabled: Boolean(workspaceId && missionId),
    staleTime: 15_000,
    queryFn: async (): Promise<Mission> => {
      const server = await api.getMission(workspaceId!, missionId!);
      if (userId) {
        return reconcileMission(userId, server, null);
      }
      return server;
    },
  });
}

// Generic side-effect helper — refreshes the LS mirror on any
// successful server mutation.
function refreshLocal(userId: string | null, mission: Mission): Mission {
  if (userId) writeLocalMission(userId, mission);
  return mission;
}

export function useStartMission(workspaceId: string | null, userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      missionType: string;
      subject_id?: string | null;
      initial_memory?: Record<string, unknown> | null;
      priority?: number;
    }) => {
      const m = await api.startMission(workspaceId!, input.missionType, {
        subject_id: input.subject_id ?? null,
        initial_memory: input.initial_memory ?? null,
        priority: input.priority ?? 0,
      });
      return refreshLocal(userId, m);
    },
    onSuccess: (mission) => {
      qc.setQueryData(missionKeys.detail(workspaceId, mission.id), mission);
      void qc.invalidateQueries({ queryKey: missionKeys.list(workspaceId) });
    },
  });
}

export function useAdvanceMission(workspaceId: string | null, userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      missionId: string;
      expected_version: number;
      next_stage?: MissionStage;
      progress?: MissionProgress;
      memory_patch?: Record<string, unknown>;
    }) => {
      const m = await api.advanceMission(workspaceId!, input.missionId, {
        expected_version: input.expected_version,
        next_stage: input.next_stage,
        progress: input.progress,
        memory_patch: input.memory_patch,
      });
      return refreshLocal(userId, m);
    },
    onSuccess: (mission) => {
      qc.setQueryData(missionKeys.detail(workspaceId, mission.id), mission);
      void qc.invalidateQueries({ queryKey: missionKeys.list(workspaceId) });
      void qc.invalidateQueries({
        queryKey: missionKeys.events(workspaceId, mission.id),
      });
    },
  });
}

export function usePauseMission(workspaceId: string | null, userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { missionId: string; expected_version: number }) => {
      const m = await api.pauseMission(workspaceId!, input.missionId, input.expected_version);
      return refreshLocal(userId, m);
    },
    onSuccess: (mission) => {
      qc.setQueryData(missionKeys.detail(workspaceId, mission.id), mission);
      void qc.invalidateQueries({ queryKey: missionKeys.list(workspaceId) });
    },
  });
}

export function useResumeMission(workspaceId: string | null, userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { missionId: string; expected_version: number }) => {
      const m = await api.resumeMission(workspaceId!, input.missionId, input.expected_version);
      return refreshLocal(userId, m);
    },
    onSuccess: (mission) => {
      qc.setQueryData(missionKeys.detail(workspaceId, mission.id), mission);
      void qc.invalidateQueries({ queryKey: missionKeys.list(workspaceId) });
    },
  });
}

export function useCompleteMission(workspaceId: string | null, userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      missionId: string;
      expected_version: number;
      final_memory_patch?: Record<string, unknown>;
    }): Promise<MissionCompleteResponse> => {
      const res = await api.completeMission(workspaceId!, input.missionId, {
        expected_version: input.expected_version,
        final_memory_patch: input.final_memory_patch,
      });
      if (userId) {
        // Purge LS on completion — the mission is terminal.
        clearLocalMission(
          userId, res.mission.workspace_id,
          res.mission.mission_type, res.mission.id,
        );
      }
      return res;
    },
    onSuccess: (res) => {
      qc.setQueryData(
        missionKeys.detail(workspaceId, res.mission.id), res.mission,
      );
      void qc.invalidateQueries({ queryKey: missionKeys.list(workspaceId) });
    },
  });
}

export function useCancelMission(workspaceId: string | null, userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { missionId: string; expected_version: number }) => {
      const m = await api.cancelMission(workspaceId!, input.missionId, input.expected_version);
      if (userId) {
        clearLocalMission(userId, m.workspace_id, m.mission_type, m.id);
      }
      return m;
    },
    onSuccess: (mission) => {
      qc.setQueryData(missionKeys.detail(workspaceId, mission.id), mission);
      void qc.invalidateQueries({ queryKey: missionKeys.list(workspaceId) });
    },
  });
}
